import type { FullPathwaySnapshot, PathwayEngineSnapshot, AhtChainSnapshot } from "./types";

const SENTINEL_SECTION = "__pathway_engine_metadata__";

const GROUP_IDS = {
  metadata: "00000000-0000-4000-a000-000000000001",
  answers: "00000000-0000-4000-a000-000000000002",
  context: "00000000-0000-4000-a000-000000000003",
  elimination: "00000000-0000-4000-a000-000000000004",
  chain: "00000000-0000-4000-a000-000000000005",
} as const;

interface UserControl {
  label: string;
  value: string;
  type: string;
}

interface TemplateSection {
  groupId: string;
  sectionTitle: string;
  userControls: UserControl[];
}

interface ConsultationTemplate {
  templateName: string;
  companyID: string;
  templateSections: TemplateSection[];
}

function uc(label: string, value: unknown, type: string): UserControl {
  const v = typeof value === "string" ? value : JSON.stringify(value ?? null);
  return { label, value: v, type };
}

function buildSections(snapshot: FullPathwaySnapshot): TemplateSection[] {
  const { engine } = snapshot;

  const sections: TemplateSection[] = [
    {
      groupId: GROUP_IDS.metadata,
      sectionTitle: SENTINEL_SECTION,
      userControls: [
        uc("version", engine.version, "number"),
        uc("pathwayId", engine.pathwayId, "string"),
        uc("engineState", engine.engineState, "string"),
        uc("currentNodeId", engine.currentNodeId, "string"),
        uc("history", engine.history, "json"),
        uc("savedAt", engine.savedAt, "string"),
      ],
    },
    {
      groupId: GROUP_IDS.answers,
      sectionTitle: "__pathway_answers__",
      userControls: Object.entries(engine.answers).map(([nodeId, answer]) =>
        uc(nodeId, answer, "json"),
      ),
    },
    {
      groupId: GROUP_IDS.context,
      sectionTitle: "__pathway_context__",
      userControls: [
        uc("contextValues", engine.contextValues, "json"),
        uc("audit", engine.audit, "json"),
        uc("selectedPgdId", engine.selectedPgdId, "string"),
        uc("exclusionAnswers", engine.exclusionAnswers, "json"),
        uc("capturedWeightKg", engine.capturedWeightKg, "number"),
      ],
    },
    {
      groupId: GROUP_IDS.elimination,
      sectionTitle: "__drug_elimination_state__",
      userControls: [
        uc("eligibleDrugIds", engine.eligibleDrugIds, "json"),
        uc("eliminationLog", engine.eliminationLog, "json"),
        uc("resolvedDrugId", engine.resolvedDrugId, "string"),
        uc("accumulatedFlags", engine.accumulatedFlags, "json"),
        uc("managementPlanCaptures", engine.managementPlanCaptures, "json"),
        uc("preferenceCaptures", engine.preferenceCaptures, "json"),
      ],
    },
  ];

  if (snapshot.chain) {
    sections.push({
      groupId: GROUP_IDS.chain,
      sectionTitle: "__aht_chain_state__",
      userControls: [
        uc("ahtChainPathwayId", snapshot.chain.ahtChainPathwayId, "string"),
        uc("ahtChainContext", snapshot.chain.ahtChainContext, "json"),
        uc("ahtChainSections", snapshot.chain.ahtChainSections, "json"),
      ],
    });
  }

  return sections;
}

export function serializeToTemplate(
  snapshot: FullPathwaySnapshot,
  templateName: string,
  companyID: string,
): ConsultationTemplate {
  return {
    templateName,
    companyID,
    templateSections: buildSections(snapshot),
  };
}

export function serializeToSections(snapshot: FullPathwaySnapshot): TemplateSection[] {
  return buildSections(snapshot);
}

function readControl(controls: UserControl[], label: string): string | undefined {
  return controls.find((c) => c.label === label)?.value;
}

function parseJson<T>(raw: string | undefined, fallback: T): T {
  if (raw === undefined || raw === "") return fallback;
  try {
    return JSON.parse(raw) as T;
  } catch {
    return fallback;
  }
}

export function deserializeFromTemplate(
  template: ConsultationTemplate | null | undefined,
): FullPathwaySnapshot | null {
  if (!template?.templateSections) {
    return null;
  }

  const metadataSection = template.templateSections.find(
    (s) => s.sectionTitle === SENTINEL_SECTION,
  );
  if (!metadataSection) {
    return null;
  }

  const mc = metadataSection.userControls;
  const version = Number(readControl(mc, "version") ?? "0");
  if (version !== 1) {
    return null;
  }

  const pathwayId = readControl(mc, "pathwayId") ?? "";
  const engineState = (readControl(mc, "engineState") ?? "consulting") as PathwayEngineSnapshot["engineState"];
  const currentNodeId = readControl(mc, "currentNodeId") ?? "";
  const history = parseJson<string[]>(readControl(mc, "history"), []);
  const savedAt = readControl(mc, "savedAt") ?? "";

  const answersSection = template.templateSections.find(
    (s) => s.sectionTitle === "__pathway_answers__",
  );
  const answers: Record<string, unknown> = {};
  if (answersSection) {
    for (const ctrl of answersSection.userControls) {
      answers[ctrl.label] = parseJson(ctrl.value, ctrl.value);
    }
  }

  const contextSection = template.templateSections.find(
    (s) => s.sectionTitle === "__pathway_context__",
  );
  const cc = contextSection?.userControls ?? [];
  const contextValues = parseJson<Record<string, unknown>>(readControl(cc, "contextValues"), {});
  const audit = parseJson<AuditRecord | null>(readControl(cc, "audit"), null);
  const selectedPgdId = parseJson<string | null>(readControl(cc, "selectedPgdId"), null);
  const exclusionAnswers = parseJson<Record<string, "yes" | "no">>(readControl(cc, "exclusionAnswers"), {});
  const capturedWeightKg = parseJson<number | null>(readControl(cc, "capturedWeightKg"), null);

  const elimSection = template.templateSections.find(
    (s) => s.sectionTitle === "__drug_elimination_state__",
  );
  const ec = elimSection?.userControls ?? [];
  const eligibleDrugIds = parseJson<string[]>(readControl(ec, "eligibleDrugIds"), []);
  const eliminationLog = parseJson(readControl(ec, "eliminationLog"), []);
  const resolvedDrugId = parseJson<string | null>(readControl(ec, "resolvedDrugId"), null);
  const accumulatedFlags = parseJson<string[]>(readControl(ec, "accumulatedFlags"), []);
  const managementPlanCaptures = parseJson(readControl(ec, "managementPlanCaptures"), []);
  const preferenceCaptures = parseJson(readControl(ec, "preferenceCaptures"), []);

  if (!pathwayId || !currentNodeId) {
    return null;
  }

  const engine: PathwayEngineSnapshot = {
    version: 1,
    pathwayId,
    savedAt,
    engineState,
    currentNodeId,
    history,
    answers,
    contextValues,
    audit,
    selectedPgdId,
    exclusionAnswers,
    capturedWeightKg,
    eligibleDrugIds,
    eliminationLog,
    resolvedDrugId,
    accumulatedFlags,
    managementPlanCaptures,
    preferenceCaptures,
  };

  const chainSection = template.templateSections.find(
    (s) => s.sectionTitle === "__aht_chain_state__",
  );
  let chain: AhtChainSnapshot | undefined;
  if (chainSection) {
    const chc = chainSection.userControls;
    chain = {
      ahtChainPathwayId: readControl(chc, "ahtChainPathwayId") ?? "aht-wt-selector",
      ahtChainContext: parseJson<Record<string, unknown>>(readControl(chc, "ahtChainContext"), {}),
      ahtChainSections: parseJson(readControl(chc, "ahtChainSections"), []),
    };
  }

  return { engine, chain };
}
